import React, { useEffect, useState } from "react";
import { listEconomicCalendar, fetchEconomicEventHistory } from "../../api/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import PageBackButton from "../../components/PageBackButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const DEFAULT_CURRENCIES = ["USD", "JPY", "EUR", "AUD", "CAD", "CHF", "GBP"];
const DEFAULT_IMPACTS = ["Medium", "High"]; // Capitalized to match sample API

function buildParams({ dateFrom, dateTo, currencies, impacts, search }) {
  const params = new URLSearchParams();
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  if (currencies?.length) params.append("currency", currencies.join(","));
  if (impacts?.length) params.append("impact", impacts.join(","));
  if (search) params.append("search", search);
  return params;
}

function getCurrentWeekRange() {
  // Compute Monday to Sunday of the current week in local time, then format as YYYY-MM-DD (UTC-based)
  const now = new Date();
  const day = now.getDay(); // 0 Sun - 6 Sat
  const diffToMonday = (day === 0 ? -6 : 1) - day; // days to Monday
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

function formatUtc(ts) {
  try {
    const d = new Date(ts);
    return d.toISOString().replace("T", " ").replace("Z", " UTC");
  } catch (e) {
    return ts;
  }
}

function last12MonthsRange() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 12);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

function parseEconomicNumber(raw) {
  if (raw === null || raw === undefined) return NaN;
  if (typeof raw === 'number') return raw;
  const s = String(raw).trim();
  if (s === '' || s.toLowerCase() === 'n/a') return NaN;
  const match = s.match(/^(-?\d+(?:[\.,]\d+)?)([kKmMbB%]?)$/);
  if (!match) {
    // Try simple parseFloat as a fallback
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }
  let num = parseFloat(match[1].replace(',', '.'));
  const suf = match[2].toLowerCase();
  if (suf === 'k') num *= 1e3;
  if (suf === 'm') num *= 1e6;
  if (suf === 'b') num *= 1e9;
  // ignore % suffix for scale; still plot raw numeric
  return num;
}

export default function EconomicCalendarPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [impacts, setImpacts] = useState(DEFAULT_IMPACTS);
  const [search, setSearch] = useState("");

  // Chart modal state
  const [chartOpen, setChartOpen] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [chartTitle, setChartTitle] = useState("");
  const [chartData, setChartData] = useState([]);

  // On first load, set defaults to current week and fetch
  useEffect(() => {
    const { from, to } = getCurrentWeekRange();
    setDateFrom(from);
    setDateTo(to);
    fetchData({ dateFrom: from, dateTo: to, currencies: DEFAULT_CURRENCIES, impacts: DEFAULT_IMPACTS, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (overrides = {}) => {
    try {
      setLoading(true);
      setError(null);
      const currentPage = overrides.page ?? page;
      const params = buildParams({
        dateFrom: overrides.dateFrom ?? dateFrom,
        dateTo: overrides.dateTo ?? dateTo,
        currencies: overrides.currencies ?? currencies,
        impacts: overrides.impacts ?? impacts,
        search: overrides.search ?? search,
      });
      params.append("page", currentPage);
      params.append("page_size", pageSize);
      const res = await listEconomicCalendar(params);
      // Paginated response: { count, next, previous, results }
      if (res && typeof res.count === "number" && Array.isArray(res.results)) {
        setEvents(res.results);
        setTotalCount(res.count);
      } else {
        const items = Array.isArray(res) ? res : [];
        setEvents(items);
        setTotalCount(items.length);
      }
    } catch (e) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setCurrencies([]);
    setImpacts([]);
    setPage(1);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    fetchData({ page: p });
  };

  const impactOptions = ["Low", "Medium", "High"];
  const currencyOptions = [
    "USD","EUR","GBP","JPY","AUD","CAD","CHF","NZD","CNY","SEK","NOK"
  ];

  const toggleInArray = (arr, setArr, value) => {
    if (arr.includes(value)) setArr(arr.filter(v => v !== value));
    else setArr([...arr, value]);
  };

  const openChart = async (eventName, currency) => {
    setChartOpen(true);
    setChartLoading(true);
    setChartError(null);
    setChartTitle(`${eventName} (${currency})`);

    try {
      const { from, to } = last12MonthsRange();
      const res = await fetchEconomicEventHistory(eventName, currency, from, to);
      const rows = Array.isArray(res) ? res : res?.data || [];
      // Build points with both actual and forecast values
      const points = rows
        .filter(r => r.event === eventName && r.currency === currency)
        .map(r => ({
          date: r.event_time ? r.event_time.slice(0, 10) : r.date || "",
          actual: parseEconomicNumber(r.actual),
          forecast: parseEconomicNumber(r.forecast),
        }))
        // keep points where at least one series has a number
        .filter(p => !(Number.isNaN(p.actual) && Number.isNaN(p.forecast)))
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      setChartData(points);
    } catch (e) {
      setChartError(e.message || "Failed to load chart data");
    } finally {
      setChartLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
        <h1 className="text-2xl font-semibold">Economic Calendar</h1>
      </div>

      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div className="lg:col-span-2">
            <label className="block text-sm mb-2">Currencies</label>
            <div className="flex flex-wrap gap-2">
              {currencyOptions.map(c => (
                <Button
                  key={c}
                  type="button"
                  variant={currencies.includes(c) ? "default" : "secondary"}
                  size="sm"
                  onClick={() => toggleInArray(currencies, setCurrencies, c)}
                >
                  {c}
                </Button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 md:col-span-2">
            <label className="block text-sm mb-2">Impact</label>
            <div className="flex flex-wrap gap-2">
              {impactOptions.map(i => (
                <Button
                  key={i}
                  type="button"
                  variant={impacts.includes(i) ? "default" : "secondary"}
                  size="sm"
                  onClick={() => toggleInArray(impacts, setImpacts, i)}
                >
                  {i}
                </Button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <label className="block text-sm mb-1">Search</label>
            <Input placeholder="Search event" value={search} onChange={e=>setSearch(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setPage(1); fetchData({ page: 1 }); }}>Apply</Button>
          <Button variant="secondary" onClick={clearFilters}>Clear</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-secondary/50">
              <tr>
                <th className="text-left p-3">Time (UTC)</th>
                <th className="text-left p-3">Currency</th>
                <th className="text-left p-3">Impact</th>
                <th className="text-left p-3">Event</th>
                <th className="text-left p-3">Actual</th>
                <th className="text-left p-3">Forecast</th>
                <th className="text-left p-3">Previous</th>
                <th className="text-left p-3">Graph</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td className="p-3" colSpan={8}>Loading...</td></tr>
              )}
              {error && !loading && (
                <tr><td className="p-3 text-red-400" colSpan={8}>{error}</td></tr>
              )}
              {!loading && !error && events.length === 0 && (
                <tr><td className="p-3" colSpan={8}>No data</td></tr>
              )}
              {!loading && !error && events.map(ev => (
                <tr key={ev.id} className="border-t border-border/40">
                  <td className="p-3">{formatUtc(ev.event_time)}</td>
                  <td className="p-3">{ev.currency}</td>
                  <td className="p-3">{ev.impact}</td>
                  <td className="p-3">{ev.event}</td>
                  <td className="p-3">{ev.actual ?? '-'}</td>
                  <td className="p-3">{ev.forecast ?? '-'}</td>
                  <td className="p-3">{ev.previous ?? '-'}</td>
                  <td className="p-3">
                    {ev.previous ? (
                      <Button size="sm" onClick={() => openChart(ev.event, ev.currency)}>View graph</Button>
                    ) : (
                      <span className="text-textSecondary">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
            <span className="text-sm text-textSecondary">
              {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} events
            </span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(1)} className="px-2">First</Button>
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)} className="px-2">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {(() => {
                const pages = [];
                let start = Math.max(1, page - 2);
                let end = Math.min(totalPages, start + 4);
                start = Math.max(1, end - 4);
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <Button key={i} variant={i === page ? "default" : "ghost"} size="sm" className="px-3 min-w-[2rem]" onClick={() => goToPage(i)}>{i}</Button>
                  );
                }
                return pages;
              })()}
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} className="px-2">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(totalPages)} className="px-2">Last</Button>
            </div>
          </div>
        )}
      </Card>

      <Modal open={chartOpen} onClose={() => setChartOpen(false)} title={chartTitle}>
        {chartLoading && <div className="p-4">Loading...</div>}
        {chartError && !chartLoading && <div className="p-4 text-red-400">{chartError}</div>}
        {!chartLoading && !chartError && chartData.length === 0 && (
          <div className="p-4">No data for the past 12 months.</div>
        )}
        {!chartLoading && !chartError && chartData.length > 0 && (
          <div className="h-80 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} domain={["auto", "auto"]} />
                <Tooltip formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : v)} />
                <Line type="monotone" dataKey="actual" name="Actual" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#0ea5e9" strokeWidth={2} dot={false} strokeDasharray="4 4" strokeOpacity={0.7} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Modal>
    </div>
  );
}
